import './account-contact.css';

import EventEmitter from 'eventemitter3';
import { Modal } from '../modal'
import { ProfileService } from '../../../services/profile-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { Data } from '../../../models/data';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { IUser } from '../../../models/user.interface';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { BirthdayRequirement, FieldType, LoyaltyProvider, Modals } from '../../../types/enums';
import { TimedMessage } from '../../shared/TimedMessage/timed-message';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { InputValidator } from '../../../validators/input-validator';
import { IValidationField } from '@ts/validators/validation-field.interface';

export interface IAccountContactProps {
    fromCheckout?: boolean;
}

type ErrorCase = {
    type: FieldType;
    inputId: string;
    errorId: string;
    extraIds?: string[];
};

/**
 * Manages the Account Contact modal for user profile updates.
 */
export class AccountContact extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;
    private _item: IUser;
    private _bindingHelper?: TwoWayBindingHelper<IUser> | undefined;
    private _options?: IAccountContactProps;
    private readonly _submit: HTMLElement | null;
    private readonly _defaultBirthYear = 2000;

    /**
     * Initializes an instance of the AccountContact class.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The service managing application data.
     * @param {ProfileService} profileService - The service managing user profiles.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService) {
        super("modal_account_contact", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Edit Account Information" }, Modals.ACCOUNT_CONTACT);
        this._data = data;
        this._profileService = profileService;
        this._submit = document.getElementById("btn_account_contact_submit");
        this._item = {} as IUser;
        this._setEventBusListener();
    }

    /**
     * Initializes the modal and sets up event listeners.
     */
    public override init(): void {
        super.init();
        this._setEventListeners();
    }

    /**
     * Opens the modal and sets necessary options.
     */
    public override open(): void {
        super.open();
        this._configureOptions();
        this._setTwoWayListeners();
        this._clearErrorMessages();
        this._populateBirthdaySelectLists();
        Util.ensureAnnouncerPresent();
    }

    /**
     * Closes the modal and resets options.
     */
    public override close(): void {
        super.close();
        this._resetOptions();
    }

    /**
     * Sets up event listeners for the submit button.
     */
    protected _setEventListeners(): void {
        this._submit?.addEventListener("click", this._submitUpdateAsync);
    }

    /**
     * Listens for modal-related events on the event bus.
     */
    protected _setEventBusListener = (): void => {
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options?: IAccountContactProps) => {
            if (modal === Modals.ACCOUNT_CONTACT)
                this._options = options;
        });
    }

    /**
     * Configures modal options based on where it's accessed from.
     */
    private _configureOptions(): void {
        if (this._options?.fromCheckout)
            Util.hideElement("div_account_contact_birthday");
        else
            this._configureBirthdaySetting();
    }

    /**
     * Configures visibility of the birthday input section on the signup form
     * based on the birthday requirement setting in brand configuration.
     */
    private _configureBirthdaySetting(): void {
        const birthdayRequirement = this._data.getBrandProperty("Settings").OOS.birthdayField;
        const labelBirthday = document.getElementById("lbl_account_contact_birthday");

        switch (birthdayRequirement) {
            case BirthdayRequirement.DO_NOT_SHOW:
                Util.hideElement("div_account_contact_birthday");
                break;
            case BirthdayRequirement.OPTIONAL:
                Util.showElement("div_account_contact_birthday");
                Util.showElement("span_account_contact_birthday_optional");
                if (labelBirthday)
                    labelBirthday.classList.remove("required-star");
                break;
            case BirthdayRequirement.REQUIRED:
                Util.showElement("div_account_contact_birthday");
                Util.hideElement("span_account_contact_birthday_optional");
                if (labelBirthday)
                    labelBirthday.classList.add("required-star");
                break;
        }
    }

    /**
     * Resets modal options after closing.
     */
    private _resetOptions(): void {
        Util.showElement("div_account_contact_birthday");
        this._options = undefined;
    }

    /**
     * Sets up two-way data binding for the form fields.
     */
    private _setTwoWayListeners(): void {
        this._item = this._profileService.user.user;
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._bindingHelper.bindToInput("Name", "txt_account_contact_name");
        this._bindingHelper.bindToInput("LastName", "txt_account_contact_lname");
        this._bindingHelper.bindToInput("Phone", "txt_account_contact_phone");
        // this._bindingHelper.bindToInput("MarketingPhone", "txt_account_contact_phone");
        this._bindingHelper.bindToInput("CountryCodeId", "select_account_contact_phone_country");
        // this._bindingHelper.bindToInput("MarketingCountryId", "select_account_contact_phone_country");
        this._bindingHelper.bindToInput("Email", "txt_account_contact_email");
        Util.populateCountryCodeSelectList("select_account_contact_phone_country", "txt_account_contact_phone", this._data.getBrandProperty("Countries"), this._profileService.user.getProperty("CountryCodeId") || 1)
    }

    /**
     * Clears all error messages from the form fields.
     */
    private _clearErrorMessages(): void {
        Util.hideElementErrors("txt_account_contact_name", "spn_account_contact_name_error");
        Util.hideElementErrors("txt_account_contact_name", "spn_account_contact_name_empty_error");
        Util.hideElementErrors("txt_account_contact_lname", "spn_account_contact_lname_error");
        Util.hideElementErrors("txt_account_contact_lname", "spn_account_contact_lname_empty_error");
        Util.hideElementErrors("txt_account_contact_phone", "spn_account_contact_phone_error");
        Util.hideElementErrors("txt_account_contact_phone", "spn_account_contact_phone_same_error");
        Util.hideElementErrors("txt_account_contact_phone", "spn_account_contact_phone_empty_error");
        Util.hideElementErrors("txt_account_contact_email", "spn_account_contact_email_error");
        Util.hideElementErrors("txt_account_contact_email", "spn_account_contact_email_same_error");
        Util.hideElementErrors("txt_account_contact_email", "spn_account_contact_email_empty_error");
        Util.hideElementErrors("select_account_contact_birthday_month", "spn_account_contact_birthday_error");
        Util.hideElementErrors("select_account_contact_birthday_day", "spn_account_contact_birthday_error");
        Util.hideElementErrors("select_account_contact_birthday_month", "spn_account_contact_birthday_month_error");
        Util.hideElementErrors("select_account_contact_birthday_day", "spn_account_contact_birthday_day_error");
        document.getElementById("select_phone_country")?.classList.toggle("invalid-input", false);
    }

    /**
     * Validates submitted data before processing the update.
     * @returns {Promise<boolean>} - Whether the submitted data is valid.
     */
    private async _validateSubmittedDataAsync(): Promise<boolean> {
        this._clearErrorMessages();

        const validationFields: IValidationField[] = [
            this._item.Name === ""
                ? { type: FieldType.FAILED, inputId: "txt_account_contact_name", errorId: "spn_account_contact_name_empty_error" }
                : { type: FieldType.NAME, inputId: "txt_account_contact_name", errorId: "spn_account_contact_name_error" },
            this._item.LastName === ""
                ? { type: FieldType.FAILED, inputId: "txt_account_contact_lname", errorId: "spn_account_contact_lname_empty_error" }
                : { type: FieldType.NAME, inputId: "txt_account_contact_lname", errorId: "spn_account_contact_lname_error" },
            this._item.Phone === ""
                ? { type: FieldType.FAILED, inputId: "txt_account_contact_phone", errorId: "spn_account_contact_phone_empty_error", extraIds: ["select_account_contact_phone_country"] }
                : { type: FieldType.PHONE, inputId: "txt_account_contact_phone", errorId: "spn_account_contact_phone_error", extraIds: ["select_account_contact_phone_country"] },
            this._item.Email === ""
                ? { type: FieldType.FAILED, inputId: "txt_account_contact_email", errorId: "spn_account_contact_email_empty_error" }
                : { type: FieldType.EMAIL, inputId: "txt_account_contact_email", errorId: "spn_account_contact_email_error" }
        ];

        const birthdayRequirement = this._data.getBrandProperty("Settings").OOS.birthdayField;

        if (birthdayRequirement === BirthdayRequirement.REQUIRED) {
            validationFields.push(
                {
                    type: FieldType.CUSTOM,
                    inputId: "select_account_contact_birthday_month",
                    errorId: "spn_account_contact_birthday_month_error",
                    extraData: [this._birthdaySelectValidator],
                },
                {
                    type: FieldType.CUSTOM,
                    inputId: "select_account_contact_birthday_day",
                    errorId: "spn_account_contact_birthday_day_error",
                    extraData: [this._birthdaySelectValidator],
                },
            );
        }

        if (birthdayRequirement !== BirthdayRequirement.DO_NOT_SHOW && !this._options?.fromCheckout && !this._isBirthdayValid()) {
            validationFields.push({
                type: FieldType.FAILED,
                inputId: "select_account_contact_birthday_month",
                errorId: "spn_account_contact_birthday_error",
                extraIds: ["select_account_contact_birthday_day"],
            });
        }

        const failed = !InputValidator.validateFields(validationFields, true, true, this._localizationService, this._dataLayer);

        if (failed)
            return false;

        let profileExists = 0;
        if (this._profileService.loyaltyProvider.getLoyaltyProvider() != LoyaltyProvider.PUNCHH)
            profileExists = await this._profileService.checkIfLoggedInExists(this._item.ProfileId, this._item.Email || "", Util.cleanNonDigits(this._item.Phone || ""), this._item.CountryCodeId || 1);

        const errorCases: Record<string, ErrorCase[]> = {
            "-2": [{ type: FieldType.FAILED, inputId: "txt_account_contact_phone", errorId: "spn_account_contact_phone_same_error", extraIds: ["select_account_contact_phone_country"] }],
            "-1": [{ type: FieldType.FAILED, inputId: "txt_account_contact_email", errorId: "spn_account_contact_email_same_error" }],
            "-3": [
                { type: FieldType.FAILED, inputId: "txt_account_contact_phone", errorId: "spn_account_contact_phone_same_error", extraIds: ["select_account_contact_phone_country"] },
                { type: FieldType.FAILED, inputId: "txt_account_contact_email", errorId: "spn_account_contact_email_same_error" }
            ]
        };

        if (profileExists in errorCases) {
            return !errorCases[profileExists.toString() as keyof typeof errorCases].map(field =>
                InputValidator.validate(field.type, field.inputId, field.errorId, true, true, undefined, field.extraIds)
            ).some(result => result);
        }

        return true;
    }

    private readonly _birthdaySelectValidator = (value: string): boolean => {
        const numericValue = Number.parseInt(value);
        return !Number.isNaN(numericValue) && numericValue !== -1;
    }

    /**
     * Handles the submit action for updating the user's account contact details.
     */
    private readonly _submitUpdateAsync = async (): Promise<void> => {
        Util.resetAnnounceCycle();

        // Suppress per-field announces during submit validation
        Util.beginBatchAnnouncement();
        const isValid = await this._validateSubmittedDataAsync();
        Util.endBatchAnnouncement();

        if (!isValid) {
            Util.scrollToFirstInvalidElement("modal_account_contact");

            Util.announceErrors(Util.collectVisibleErrors("modal_account_contact"));
            return;
        }

        this._item.Name = this._item.Name?.trim() || "";
        this._item.LastName = this._item.LastName?.trim() || "";
        this._item.Phone = Util.cleanNonDigits(this._item.Phone || "");
        this._item.MarketingPhone = this._item.Phone;

        const birthdayRequirement = this._data.getBrandProperty("Settings").OOS.birthdayField;

        if (birthdayRequirement !== BirthdayRequirement.DO_NOT_SHOW && !this._options?.fromCheckout) {
            const birthDate = this._getBirthDateFromSelectors();
            this._item.BirthDate = birthDate;
        }

        const loader = new LoaderSpinner();
        const success = await this._profileService.saveProfile({ ...this._item } as IUser, () => { }, false);
        loader?.destroy();

        if (success) {
            this.close();

            const loader = new LoaderSpinner();
            await this._profileService.signInJWT();
            loader?.destroy();

            const msg = this._localizationService.names("AccountUpdateSuccess");
            new TimedMessage(msg, "", 1500).show();

            if (this._dataLayer)
                this._dataLayer.update_profile(this._item);
        } else {
            const err = this._localizationService.names("AccountUpdateFailure");
            new TimedMessage(err, "", 1500).show();

            Util.announceErrors([err]);
        }
    };

    private _populateBirthdaySelectLists(): void {
        const currentLanguage = this._context.getProperty("languageCode") || "en-US";
        const year = this._defaultBirthYear;

        this._populateBirthdayMonthSelectList(year, currentLanguage);
        this._populateBirthdayDaySelectList(year, -1); // Default to not-selected value

        if (this._item.BirthDate)
            this._setBirthdaySelectors(this._item.BirthDate);
    }

    private _populateBirthdayMonthSelectList(year: number, language: string): void {
        const monthSelect = document.getElementById("select_account_contact_birthday_month") as HTMLSelectElement;

        Util.populateMonthSelectList(monthSelect, year, language, this._localizationService);

        monthSelect.addEventListener("change", (event) => {
            const selectedMonth = Number.parseInt((event.target as HTMLSelectElement).value);
            this._populateBirthdayDaySelectList(year, selectedMonth);
        });
    }

    private _populateBirthdayDaySelectList(year: number,month: number): void {
        const daySelect = document.getElementById("select_account_contact_birthday_day") as HTMLSelectElement;
        Util.populateDaySelectList(daySelect, year, month, this._localizationService);
    }

    private _isBirthdayValid(): boolean {
        const selectedMonth = Number.parseInt((document.getElementById("select_account_contact_birthday_month") as HTMLSelectElement).value);
        const selectedDay = Number.parseInt((document.getElementById("select_account_contact_birthday_day") as HTMLSelectElement).value);

        if (selectedMonth == -1 || selectedDay == -1)
            return true; // as a valid not-selected state

        return Util.isValidDate(this._defaultBirthYear, selectedMonth, selectedDay);
    }

    private _setBirthdaySelectors(birthDate: Date): void {
        const monthSelect = document.getElementById("select_account_contact_birthday_month") as HTMLSelectElement;
        const daySelect = document.getElementById("select_account_contact_birthday_day") as HTMLSelectElement;

        const month = birthDate.getUTCMonth() + 1; // getUTCMonth is zero-based
        monthSelect.value = month.toString();
        this._populateBirthdayDaySelectList(this._defaultBirthYear, month);
        daySelect.value = birthDate.getUTCDate().toString();
    }

    private _getBirthDateFromSelectors(): Date | null {
        const selectedMonth = Number.parseInt((document.getElementById("select_account_contact_birthday_month") as HTMLSelectElement).value);
        const selectedDay = Number.parseInt((document.getElementById("select_account_contact_birthday_day") as HTMLSelectElement).value);

        if (selectedMonth == -1 || selectedDay == -1)
            return null;

        if (Util.isValidDate(this._defaultBirthYear, selectedMonth, selectedDay)) {
            return new Date(Date.UTC(this._defaultBirthYear, selectedMonth - 1, selectedDay));
        }

        return null;
    }
}